/**
 * Settings UI primitives — reused across all settings sections.
 */

import { useState, type ReactNode } from "react";

export function SettingsGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: "var(--space-8)" }}>
      <div style={{
        fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)",
        color: "var(--text-muted)", letterSpacing: "0.06em",
        textTransform: "uppercase", marginBottom: "var(--space-4)",
        paddingBottom: "var(--space-2)",
        borderBottom: "1px solid var(--border-subtle)",
      }}>
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {children}
      </div>
    </div>
  );
}

export function SettingsRow({ label, description, children }: {
  label: string; description?: string; children: ReactNode;
}) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", justifyContent: "space-between",
      gap: "var(--space-6)",
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--text-primary)" }}>
          {label}
        </div>
        {description && (
          <div style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", marginTop: 2 }}>
            {description}
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0 }}>
        {children}
      </div>
    </div>
  );
}

export function Toggle({ defaultOn = false, checked, onChange }: { defaultOn?: boolean; checked?: boolean; onChange?: () => void }) {
  const [internalOn, setInternalOn] = useState(defaultOn);
  const on = checked !== undefined ? checked : internalOn;
  return (
    <div
      onClick={() => {
        if (onChange) onChange();
        else setInternalOn(v => !v);
      }}
      style={{
        width: 36, height: 20, borderRadius: "var(--radius-full)",
        background: on ? "var(--accent)" : "var(--bg-floating)",
        border: `1px solid ${on ? "var(--accent)" : "var(--border-strong)"}`,
        cursor: "pointer", position: "relative",
        transition: "background var(--duration-fast), border-color var(--duration-fast)",
      }}
    >
      <div style={{
        position: "absolute", top: 2,
        left: on ? 18 : 2,
        width: 14, height: 14,
        borderRadius: "var(--radius-full)",
        background: on ? "#000" : "var(--text-muted)",
        transition: "left var(--duration-fast) var(--ease-spring)",
      }} />
    </div>
  );
}

export const selectStyle: React.CSSProperties = {
  height: 32, padding: "0 var(--space-3)", borderRadius: "var(--radius-md)",
  background: "var(--bg-floating)", border: "1px solid var(--border-default)",
  color: "var(--text-primary)", fontSize: "var(--text-sm)", cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
